import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MedReviewDatasource } from '../../../../../core/lists/_data-sources/med-review.datasource';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../core/reducers';
import { PatientsService } from '../../../../../core/lists';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
import { clearMedReviews, loadMedReviews } from '../../actions/med-review.actions';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { MedReviewService } from '../../services/med-review.service';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, merge, Subscription } from 'rxjs';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { MedReviewAddStandardComponent } from '../med-review-add-standard/med-review-add-standard.component';
import { distinctUntilChanged, skip, take } from 'rxjs/operators';
import { OfficesService } from '../../../../../core/management';
import { MedReviewConfigComponent } from '../med-review-config/med-review-config.component';
import { FollowUpDialogComponent } from '../../../follow-up/follow-up-dialog/follow-up-dialog.component';
import { CommentsDialogComponent } from '../../../../partials/content/general/comments-dialog/comments-dialog.component';
import { CommentsService } from '../../../../../core/services/comments.service';
import { MedReviewBoardBodyComponent } from '../med-review-board-body/med-review-board-body.component';
import { AuthService } from '../../../../../core/auth';
export interface PeriodicElement {
  name: string;
  position: number;
  weight: number;
  symbol: string;
}

@Component({
  selector: 'kt-med-review-list',
  templateUrl: './med-review-list.component.html',
  styleUrls: ['./med-review-list.component.scss'],
})
export class MedReviewListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() patientId: number;
  @Input() fromPatient = false;
  displayedColumns: string[] = [];
  dataSource: MedReviewDatasource;
  isCompleted = false;
  userMap = new Map<number, any>();
  ngOnChanges(): void {
    this.updateDisplayedColumns();
  }
  private subscriptions: Subscription[] = [];
  @ViewChild('sort', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  filter: any;
  isAssign = false;
  medReviews = [];
  officeIDs = [];
  offices = [];
  loadingOffices = false;
  constructor(
    private router: Router,
    private store: Store<AppState>,
    public stringUtilsService: StringUtilsService,
    private patientsService: PatientsService,
    private medReviewService: MedReviewService,
    private officeService: OfficesService,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
    private activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    private commentsService: CommentsService,
    private authService: AuthService,
  ) {
    this.dataSource = new MedReviewDatasource(this.store);
    this.filter = this.medReviewService.getCurrentFilter();
  }

  ngOnInit(): void {
    this.updateDisplayedColumns();
    const oldPage = this.medReviewService.getCurrentPage();
    if (oldPage) {
      this.paginator.pageIndex = oldPage.pageIndex;
      this.paginator.pageSize = oldPage.pageSize;
    }
    const oldSort = this.medReviewService.getCurrentSort();
    if (oldSort) {
      this.sort.active = oldSort.active;
      this.sort.direction = oldSort.direction;
    }
    if (!this.patientId && this.fromPatient) {
      this.patientId = Number(this.activatedRoute.snapshot.paramMap.get('id'));
    }
    this.loadData(true);
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page).subscribe(
      () => {
        this.loadData();
      },
    );
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push(sortSubscription);

    this.subscriptions.push(paginatorSubscriptions);

    const officeMappingSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        this.medReviews = res;
        this.officeIDs = this.medReviews.map((x) => {
          return Number(x.office_id);
        });
        this.officeService.getOfficesByIds(this.officeIDs).subscribe((result) => {
          this.offices = result;
          this.loadingOffices = true;
        });
      });
    this.subscriptions.push(officeMappingSubscription);
    const uniqueIds = [...new Set(this.medReviews.map((x) => x.closed_by_id))];

    forkJoin(uniqueIds.map((id) => this.authService.getUserById(id))).subscribe((users) => {
      users.forEach((user) => {
        this.userMap.set(user.id, user);
      });
    });
  }

  updateDisplayedColumns() {
    this.displayedColumns = [
      'id',
      ...(!this.fromPatient ? ['patientId', 'patientName', 'facilityName'] : []),
      'startDate',
      ...(this.fromPatient ? ['endDate'] : []),
      'time',
      'facilityCycleDate',
      'indicators',
      'officeId',
      ...(this.fromPatient && this.isCompleted ? ['closedBy'] : []),
      ...(this.fromPatient ? ['actions'] : []),
    ];
  }
  getIndicators(indicators: any) {
    return this.medReviewService.getIndicators(indicators);
  }

  navicatToBoard(row) {
    this.medReviewService.updateMedReviewIndex(row.id);
    this.router.navigate(['board', row.id], { relativeTo: this.activatedRoute });
  }
  showCompleted() {
    this.store.dispatch(clearMedReviews());
    this.isCompleted = !this.isCompleted;
    this.filter = this.isCompleted === true ? { status: 'DONE' } : {};
    this.medReviewService.setCurrentFilter({ ...this.filter });
    this.loadData();
    this.updateDisplayedColumns();
    this.paginator.pageIndex = 0;
  }

  private loadData(firstLoad = false) {
    const queryParams = new QueryParamsModel(
      this.filter,
      firstLoad ? 'asc' : this.sort.direction,
      firstLoad ? 'startDate' : this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    this.fromPatient
      ? this.store.dispatch(loadMedReviews({ page: queryParams, patientId: this.patientId }))
      : this.store.dispatch(loadMedReviews({ page: queryParams }));
  }
  displaySnapShots(event: Event, element) {
    event.stopPropagation();
    this.dialog.open(MedReviewBoardBodyComponent, {
      data: element.medreview_snapshot,
    });
  }
  displayComments(event: Event, element) {
    event.stopPropagation();

    this.commentsService
      .getComments('medreviews', element.id)
      .pipe(take(1))
      .subscribe(({ data }) => {
        this.dialog.open(CommentsDialogComponent, {
          width: '450px',
          data: {
            height: 40,
            transactionType: 'medreviews',
            transactionId: element.id,
            comments: data,
            viewMode: true,
          },
        });
      });
  }
  displayFollowups(event, element) {
    event.stopPropagation();

    this.dialog.open(FollowUpDialogComponent, {
      data: {
        body: element,
        type: 'MEDREVIEW',
        title: element.patient_name,
        relatedIds: [element.patient_id],
        viewMode: true,
      },
      width: '1200px',
    });
  }

  filterChanges(value: any) {
    this.filter = value;
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  ngOnDestroy(): void {
    this.fromPatient
      ? this.medReviewService.setCurrentFilter({})
      : this.medReviewService.setCurrentFilter(this.filter);
    this.medReviewService.setCurrentSort(this.sort);
    this.medReviewService.setCurrentPage(this.paginator);
  }

  assignStandard(event) {
    this.isAssign = !this.isAssign;
    event.stopPropagation();

    const dialogRef = this.dialog.open(MedReviewAddStandardComponent, { width: '45vw' });

    dialogRef.afterClosed().subscribe((res) => {
      this.isAssign = false;
      if (!res) return;

      const message = this.translate.instant('MED_REVIEW.ASSIGN_STANDARD_MESSAGE');
      this.layoutUtils.showActionNotification(message, MessageType.Update, 3000, false, false);
    });
  }

  manageConfigs(event: Event): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '800px';

    const dialogRef = this.dialog.open(MedReviewConfigComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        const message = this.translate.instant('MED_REVIEW.UPDATE_CONFIGS_MESSAGE');
        this.layoutUtils.showActionNotification(message, MessageType.Update, 3000, false, false);
      });
  }
}
